// Components
import {
  AppLayout,
  BreadcrumbGroup,
  ContentLayout,
  Header,
  Link,
  SpaceBetween,
  Tabs,
} from '@amzn/awsui-components-react';
import { SignupList } from './signupsList';
import { ActivitySidebar } from './sideNav';
// Hooks
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
// Utils
import { externalLinks } from '../../data/externalLinks';
import {
  DISPLAY_ATTENDANCE_INFO,
  DISPLAY_DATE_INFO,
  DISPLAY_LOCATION_INFO,
} from '../shifts/shiftInfo';

type TabType = 'upcoming' | 'past';

export function ActivityPage() {
  const { t } = useTranslation(['activity', 'translation']);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const tabParam = searchParams.get('tab') as TabType | null;
  const activeTabId: TabType =
    tabParam && ['upcoming', 'past'].includes(tabParam) ? tabParam : 'upcoming';

  const urlFeatures = searchParams.get('features');
  const priorityShiftId = searchParams.get('shiftId') ?? undefined;
  const volunteerActivityUrl = `/my-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;

  return (
    <AppLayout
      footerSelector=".footer"
      navigationHide={false}
      navigation={<ActivitySidebar baseUrl={volunteerActivityUrl} />}
      toolsHide={true}
      breadcrumbs={
        <div className="breadcrumbs">
          <BreadcrumbGroup
            items={[
              { text: t('breadcrumbs.home', { ns: 'translation' }), href: '/' },
              {
                text: t('breadcrumbs.volunteerActivity'),
                href: '#/',
              },
            ]}
            onFollow={(event) => {
              navigate(event.detail.href);
              event.preventDefault();
            }}
          />
        </div>
      }
      content={
        <div className="page-content">
          <ContentLayout
            header={
              <div className="page-content-header">
                <Header
                  variant="h1"
                  actions={
                    <Link
                      data-aci-analytics-name="myActivity_reportTime"
                      href={externalLinks.support.trackTime}
                      external
                    >
                      {t('trackTime')}
                    </Link>
                  }
                >
                  {t('pageTitle.volunteerActivity')}
                </Header>
              </div>
            }
          >
            <SpaceBetween direction="vertical" size="xxl">
              <Tabs
                activeTabId={activeTabId}
                onChange={({ detail }) => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('tab', detail.activeTabId);
                  setSearchParams(newParams);
                }}
                tabs={[
                  {
                    label: t('upcoming'),
                    id: 'upcoming',
                    content: (
                      <SignupList
                        startDate={new Date(Date.now())}
                        sort="oldest-first"
                        displayAttributes={[DISPLAY_DATE_INFO, DISPLAY_LOCATION_INFO]}
                        priorityShiftId={priorityShiftId}
                      />
                    ),
                  },
                  {
                    label: t('past'),
                    id: 'past',
                    content: (
                      <SignupList
                        endDate={new Date(Date.now())}
                        sort="newest-first"
                        displayAttributes={[
                          DISPLAY_DATE_INFO,
                          DISPLAY_LOCATION_INFO,
                          DISPLAY_ATTENDANCE_INFO,
                        ]}
                        includeAttendanceOnlyShifts={true}
                      />
                    ),
                  },
                ]}
              />
            </SpaceBetween>
          </ContentLayout>{' '}
        </div>
      }
      contentType="cards"
    />
  );
}
